import React from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import RenderHtml, { MixedStyleDeclaration } from 'react-native-render-html';
import { View, useToken } from '@gluestack-ui/themed';

import { mergeHtmlTagStyles } from './mergeHtmlTagStyles';
import { prepareHtmlContent } from './prepareHtmlContent';

export { mergeHtmlTagStyles };

interface HtmlTextProps {
  content?: string;
  width?: number;
  numberOfLines?: number;
  fontSize?: number;
  contentStyle?: MixedStyleDeclaration;
  applySubmissionFormatting?: boolean;
  /** When false, block-root HTML (e.g. h2) is not wrapped in an extra leading p tag. Default true. */
  wrapPlainTextInParagraph?: boolean;
  /** Merged over default/submission tag styles (e.g. zero top margin on first heading). */
  tagsStyles?: Record<string, MixedStyleDeclaration>;
  testID?: string;
}

const HtmlText: React.FC<HtmlTextProps> = ({
  content = '',
  width,
  numberOfLines,
  fontSize,
  contentStyle,
  applySubmissionFormatting,
  wrapPlainTextInParagraph = true,
  tagsStyles: tagsStylesOverride,
  testID,
}) => {
  const { width: windowWidth } = useWindowDimensions();

  const wrappedContent = React.useMemo(
    () => prepareHtmlContent(content, wrapPlainTextInParagraph),
    [content, wrapPlainTextInParagraph],
  );

  const source = React.useMemo(() => ({ html: wrappedContent }), [wrappedContent]);
  const fontFamily = useToken('fonts', 'body') as string;
  const defaultFontSize = (useToken('fontSizes', 'md') as number) || 14; // Fallback to 14 if token not found
  // @ts-expect-error - textBody is a semantic token
  const fontColor = useToken('colors', 'textBody') as string;

  const submissionFormatting: Record<string, MixedStyleDeclaration> = {
    h1: { fontSize: 16, marginTop: 0 },
    h2: { fontSize: 14, paddingBottom: 0 },
    p: {
      fontSize: 14, textAlign: 'justify', marginTop: 0, marginBottom: 15,
    },
    li: { fontSize: 14, textAlign: 'left', marginBottom: 10 },
    ul: { paddingLeft: 20, listStyleType: 'disc' },
    ol: { paddingLeft: 20, listStyleType: 'decimal' },
  };

  // Ensure we have valid numbers for font size and line height calculations
  const parseFontSize = (val: unknown): number => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const parsed = parseInt(val, 10);
      return Number.isNaN(parsed) ? defaultFontSize : parsed;
    }
    return defaultFontSize;
  };

  const lineHeightFactor = 1.4;
  const effectiveFontSize = parseFontSize(fontSize);
  const lineHeight = Math.max(1, Math.trunc(effectiveFontSize * lineHeightFactor)) || effectiveFontSize;
  const elementHeight = numberOfLines ? Math.ceil(lineHeight * (numberOfLines + 1)) : undefined;

  // on android selectable text breaks ellipsize
  const textSelectable = Platform.OS !== 'android';

  const baseStyle: MixedStyleDeclaration = {
    fontFamily,
    fontSize: effectiveFontSize,
    paddingVertical: 0,
    color: fontColor,
    margin: 0,
    // @ts-expect-error - lineHeight can be a string with px in mixed styles
    lineHeight: `${lineHeight}px`,
    ...contentStyle,
  };

  return (
    <View
      testID={testID}
      h={elementHeight}
      // @ts-expect-error - numberOfLines is not a valid prop for gluestack View but used here for some reason
      numberOfLines={numberOfLines}
      overflow="hidden"
      backgroundColor="transparent"
    >
      <RenderHtml
        contentWidth={typeof width === 'number' ? width : windowWidth}
        defaultTextProps={{
          selectable: textSelectable,
          numberOfLines,
        }}
        systemFonts={[fontFamily]}
        baseStyle={baseStyle}
        tagsStyles={mergeHtmlTagStyles(
          applySubmissionFormatting ? submissionFormatting : { p: { marginTop: 0 } },
          tagsStylesOverride,
        )}
        source={source}
      />
    </View>
  );
};

export default React.memo(HtmlText);
